import React, { useState, useEffect } from 'react';
import { 
  Flame, 
  Target, 
  Award, 
  BrainCircuit, 
  Play, 
  Sparkles, 
  ChevronRight, 
  TrendingUp, 
  Clock, 
  RotateCcw,
  BookOpen,
  Calendar,
  AlertTriangle,
  HelpCircle,
  BarChart3,
  CheckCircle2,
  ArrowUpRight,
  Star
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine
} from 'recharts';
import { UserProfile, TestAttempt, Question, QuestionMastery, PraxisPaper } from '../types';
import { STATE_STANDARDS } from '../data/stateStandards';
import { PRAXIS_PAPERS } from '../data/praxisPapers';
import { examCatalog, ExamCatalogEntry } from '../data/examCatalog';
import { getUserQuestionMastery, getQuestionsForStudiedPapers } from '../lib/firebase';

// Same family/paper matching heuristic StudyGuides.tsx / AuthModal.tsx use to
// resolve an Exam Catalog family entry to its concrete PraxisPapers --
// duplicated here so a user's signup goal exam (UserProfile.targetPaperId)
// can be pinned to the front of this dashboard's family grid and drive the
// default stats paper, the exact same way elsewhere in the app resolves it.
function paperMatchesExamCode(paper: PraxisPaper, targetExamCodeRaw: string): boolean {
  const paperExamCode = paper.examCode ? paper.examCode.trim() : '';
  const targetExamCode = targetExamCodeRaw.trim();

  if (paperExamCode && paperExamCode.toLowerCase() === targetExamCode.toLowerCase()) {
    return true;
  }
  if (paperExamCode) {
    const lowerP = paperExamCode.toLowerCase();
    const lowerTarget = targetExamCode.toLowerCase();
    if (lowerP.startsWith(lowerTarget + '-') || lowerP === lowerTarget) {
      return true;
    }
  }
  if (paper.code) {
    const lowerCode = paper.code.trim().toLowerCase();
    const lowerTarget = targetExamCode.toLowerCase();
    if (lowerCode.startsWith(lowerTarget + '-') || lowerCode === lowerTarget) {
      return true;
    }
  }
  if (targetExamCode === 'Praxis-Core' && paper.category === 'core' && (!paperExamCode || paperExamCode.startsWith('Praxis'))) return true;
  if (targetExamCode === 'Praxis-Elementary' && (paper.category === 'elementary' || paper.code === '5001')) return true;
  if (targetExamCode === 'Praxis-Subject' && (paper.category === 'subject' || paper.category === 'elementary')) return true;
  if (targetExamCode === 'Praxis-PLT' && paper.category === 'plt') return true;

  return false;
}

// Some Exam Catalog families (NYSTCE, TExES, FTCE, ILTS, PECT, GACE) have no
// authored PraxisPaper content yet, so AuthModal.tsx's goal-exam picker
// falls back to storing the bare family id in targetPaperId for those --
// this lookup has to handle both a real PraxisPaper id and that fallback.
function findFamilyForTargetPaperId(targetPaperId: string | undefined, papers: PraxisPaper[]): ExamCatalogEntry | null {
  if (!targetPaperId) return null;
  const paper = papers.find((p) => p.id === targetPaperId);
  if (paper) {
    const viaPaper = examCatalog.find((entry) => entry.examCodes.some((code) => paperMatchesExamCode(paper, code)));
    if (viaPaper) return viaPaper;
  }
  return examCatalog.find((entry) => entry.id === targetPaperId) || null;
}

interface DashboardProps {
  userProfile: UserProfile;
  testAttempts: TestAttempt[];
  onStartTest: (paperId: string) => void;
  onOpenGuides: (paperId: string) => void;
  openPricingModal: () => void;
  setActiveTab: (tab: string) => void;
  dueReviewCount?: number;
  onStartCustomPractice?: (questions: Question[], title: string, mode?: 'exam' | 'practice') => void;
  onSelectExam?: (entry: ExamCatalogEntry) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({
  userProfile,
  testAttempts = [],
  onStartTest,
  onOpenGuides,
  openPricingModal,
  setActiveTab,
  dueReviewCount = 0,
  onStartCustomPractice,
  onSelectExam
}) => {
  const [aiPlan, setAiPlan] = useState<string | null>(null);
  const [loadingAiPlan, setLoadingAiPlan] = useState<boolean>(false);
  const [masteryList, setMasteryList] = useState<QuestionMastery[]>([]);
  const [allQuestions, setAllQuestions] = useState<Question[]>([]);

  const safeAttempts = Array.isArray(testAttempts) ? testAttempts : [];
  const safeMasteryList = Array.isArray(masteryList) ? masteryList : [];
  const safeQuestions = Array.isArray(allQuestions) ? allQuestions : [];

  const activeState = STATE_STANDARDS.find(s => s.code === userProfile?.targetState) || STATE_STANDARDS[0];

  // Goal exam captured at signup (skippable) -- when set, it drives the
  // default stats paper below (until the user has real attempts) and gets
  // pinned to the front of the exam family grid further down this page.
  const goalPaper = userProfile?.targetPaperId
    ? PRAXIS_PAPERS.find(p => p.id === userProfile.targetPaperId) || null
    : null;
  // targetExamFamily (saved by the onboarding screener) covers goal papers
  // that only exist in Firestore, e.g. the 59 papers added in Oct 2026,
  // which the static PRAXIS_PAPERS list above does not know about.
  const goalFamily = findFamilyForTargetPaperId(userProfile?.targetPaperId, PRAXIS_PAPERS)
    || (userProfile?.targetExamFamily ? examCatalog.find(e => e.id === userProfile.targetExamFamily) || null : null);

  // Exam countdown from the onboarding screener's exam date.
  const daysToExam = (() => {
    const iso = userProfile?.targetExamDate;
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
    const [y, m, d] = iso.split('-').map(Number);
    const examDay = new Date(y, m - 1, d);
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((examDay.getTime() - todayStart.getTime()) / 86400000);
  })();
  const dailyGoalLabel = (() => {
    const mins = userProfile?.dailyGoalMinutes;
    if (!mins || mins <= 0) return '';
    if (mins < 60) return `${mins} min/day`;
    if (mins === 60) return '1 hr/day';
    if (mins >= 180) return '3+ hrs/day';
    return `${Math.round(mins / 60)} hrs/day`;
  })();

  // Load question mastery & questions pool for analytics
  useEffect(() => {
    async function loadDashboardAnalytics() {
      try {
        // 2026-10-08 speed round: the Dashboard only needs the questions this
        // student has answered (to label weak domains). It used to download
        // every question of every paper (getPraxisPapersFromFirestore); now it
        // loads just the pools of papers they have practised, plus their goal
        // exam, falling back to the bundled sample questions when nothing can
        // be read (e.g. free accounts, whose question reads are paywalled).
        let mastery: QuestionMastery[] = [];
        if (userProfile?.uid && userProfile.uid !== 'guest_user_1') {
          const loaded = await getUserQuestionMastery(userProfile.uid);
          if (Array.isArray(loaded)) {
            mastery = loaded;
            setMasteryList(loaded);
          }
        }

        const extraPaperIds = [
          userProfile?.targetPaperId || '',
          ...safeAttempts.map(a => a?.paperId || '')
        ].filter(Boolean);
        const studied = userProfile?.uid && userProfile.uid !== 'guest_user_1'
          ? await getQuestionsForStudiedPapers(userProfile.uid, mastery, extraPaperIds)
          : [];

        if (studied.length > 0) {
          setAllQuestions(studied);
        } else {
          const qPool: Question[] = [];
          for (const p of PRAXIS_PAPERS) {
            if (Array.isArray(p?.questions)) {
              qPool.push(...p.questions);
            }
          }
          setAllQuestions(qPool);
        }
      } catch (err) {
        console.warn('Dashboard: Error loading analytics data:', err);
      }
    }
    loadDashboardAnalytics();
  }, [userProfile?.uid]);

  // Calculate stats from real user data & safeAttempts. Falls back to the
  // user's goal exam (set at signup or from the profile card) before the
  // hardcoded 5713 default, so a brand-new candidate's stats headline
  // their chosen exam rather than an arbitrary paper they haven't touched.
  const recentPaperCode = safeAttempts.length > 0 && safeAttempts[0]?.paperCode
    ? safeAttempts[0].paperCode
    : (goalPaper?.code || goalPaper?.id || '5713');
  const targetPassingScore = activeState?.passingScores?.[recentPaperCode] || activeState?.passingScores?.['5713'] || 156;

  const totalAttempts = safeAttempts.length;
  
  // Calculate average ETS score (Scale 100-200) from real safeAttempts
  const avgEtsScore = totalAttempts > 0 
    ? Math.round(safeAttempts.reduce((acc, curr) => acc + (curr?.score || (100 + Math.round((curr?.percentage || 0) * 1.0))), 0) / totalAttempts)
    : null;

  // Calculate pass rate from real safeAttempts
  const passedAttemptsCount = safeAttempts.filter(t => t?.passed).length;
  const passRate = totalAttempts > 0 ? Math.round((passedAttemptsCount / totalAttempts) * 100) : null;

  // Real Study Time Logged (summing test time and userProfile totalStudyMinutes)
  const testTimeMinutes = Math.round(safeAttempts.reduce((acc, t) => acc + (t?.timeSpentSeconds || 0), 0) / 60);
  const totalStudyMinutes = (userProfile?.totalStudyMinutes || 0) + testTimeMinutes;
  const hoursLogged = Math.floor(totalStudyMinutes / 60);
  const minsLogged = totalStudyMinutes % 60;

  // Real Domain Performance Aggregation (from questionMastery AND safeAttempts.domainBreakdown)
  const questionMap = new Map<string, Question>(safeQuestions.map(q => [q.id, q]));

  // Helper function to resolve any raw key/id/text to a valid PRAXIS domain name safely
  const resolveDomainName = (rawDomainKey?: string, questionId?: string, paperCodeStr?: string): string => {
    const sRaw = typeof rawDomainKey === 'string' ? rawDomainKey : '';
    const sQid = typeof questionId === 'string' ? questionId : '';
    const sPcode = typeof paperCodeStr === 'string' ? paperCodeStr : '';

    if (sRaw && sRaw.length <= 60 && !sRaw.includes('?') &&
        !sRaw.toLowerCase().startsWith('a ') && !sRaw.toLowerCase().startsWith('in ') &&
        !sRaw.toLowerCase().startsWith('which ') && !sRaw.toLowerCase().startsWith('select ') &&
        !sRaw.startsWith('q5713') && !sRaw.startsWith('q5723') && !sRaw.startsWith('q5733')) {
      const matchesKnownDomain = PRAXIS_PAPERS.some(p => Array.isArray(p?.domains) && p.domains.some(d => d?.name === sRaw));
      if (matchesKnownDomain) return sRaw;
    }

    const lookupId = sQid || sRaw || 'core_item';
    const qObj = questionMap.get(lookupId) || safeQuestions.find(q => q?.id === lookupId || q?.questionText === sRaw);
    if (qObj?.domain && typeof qObj.domain === 'string' && qObj.domain.length <= 60 && !qObj.domain.includes('?')) {
      const matchesKnownDomain = PRAXIS_PAPERS.some(p => Array.isArray(p?.domains) && p.domains.some(d => d?.name === qObj.domain));
      if (matchesKnownDomain) return qObj.domain;
    }

    for (const p of PRAXIS_PAPERS) {
      if (Array.isArray(p?.questions)) {
        const matchQ = p.questions.find(sq => sq?.id === lookupId || sq?.questionText === sRaw || (sQid && sq?.id === sQid));
        if (matchQ?.domain && typeof matchQ.domain === 'string') return matchQ.domain;
      }
    }

    const pCode = sPcode || (lookupId.startsWith('q5713') ? '5713' : recentPaperCode);
    const paperObj = PRAXIS_PAPERS.find(p => p?.code === pCode || p?.id === pCode) || PRAXIS_PAPERS[0];
    if (paperObj && Array.isArray(paperObj.domains) && paperObj.domains.length > 0) {
      if (lookupId.endsWith('_1')) return paperObj.domains[0]?.name || 'Core Domain 1';
      if (lookupId.endsWith('_2')) return paperObj.domains[1 % paperObj.domains.length]?.name || 'Core Domain 2';
      if (lookupId.endsWith('_3')) return paperObj.domains[2 % paperObj.domains.length]?.name || 'Core Domain 3';

      let hash = 0;
      for (let i = 0; i < lookupId.length; i++) hash += lookupId.charCodeAt(i);
      const domIdx = Math.abs(hash) % paperObj.domains.length;
      return paperObj.domains[domIdx]?.name || 'Core Domain';
    }

    return (sRaw && sRaw.length <= 60) ? sRaw : 'Core Standard Domain';
  };

  const domainAggMap: Record<string, { name: string; total: number; correct: number }> = {};

  // 1. Aggregate from questionMastery
  for (const m of safeMasteryList) {
    if (!m) continue;
    const q = m.questionId ? questionMap.get(m.questionId) : undefined;
    const rawDom = (m as any).domain || q?.domain || m.questionId || '';
    const domainName = resolveDomainName(rawDom, m.questionId, recentPaperCode);

    if (!domainAggMap[domainName]) {
      domainAggMap[domainName] = { name: domainName, total: 0, correct: 0 };
    }
    const corr = m.timesCorrect || 0;
    const incorr = m.timesIncorrect || 0;
    domainAggMap[domainName].correct += corr;
    domainAggMap[domainName].total += (corr + incorr);
  }

  // 2. Aggregate from safeAttempts
  for (const attempt of safeAttempts) {
    if (attempt && attempt.domainBreakdown && typeof attempt.domainBreakdown === 'object') {
      for (const [rawKey, statsObj] of Object.entries(attempt.domainBreakdown)) {
        const stats = statsObj as { correct: number; total: number } | undefined;
        const domainName = resolveDomainName(rawKey, undefined, attempt.paperCode || recentPaperCode);

        if (!domainAggMap[domainName]) {
          domainAggMap[domainName] = { name: domainName, total: 0, correct: 0 };
          domainAggMap[domainName].correct = stats?.correct || 0;
          domainAggMap[domainName].total = stats?.total || 0;
        } else if (safeMasteryList.length === 0) {
          domainAggMap[domainName].correct += (stats?.correct || 0);
          domainAggMap[domainName].total += (stats?.total || 0);
        }
      }
    }
  }

  const realDomainList = Object.values(domainAggMap).map(d => {
    const mastery = d.total > 0 ? Math.round((d.correct / d.total) * 100) : 0;
    const color = mastery >= 75 ? 'bg-emerald-500' : mastery >= 60 ? 'bg-amber-500' : 'bg-rose-500';
    return { name: d.name, mastery, count: d.total, color };
  });

  // Sort by mastery ascending (weakest first)
  realDomainList.sort((a, b) => a.mastery - b.mastery);

  // -------------------------------------------------------------
  // INSIGHT WIDGET 1: Error Pattern Analysis
  // -------------------------------------------------------------
  let weakestDomain: { 
    domainName: string; 
    total: number; 
    correct: number; 
    incorrect: number; 
    accuracy: number; 
    missPercentage: number 
  } | null = null;

  const attemptedDomains = Object.values(domainAggMap).filter(d => d.total >= 1);

  if (attemptedDomains.length > 0) {
    const scoredDomains = attemptedDomains.map(d => {
      const accuracy = d.total > 0 ? Math.round((d.correct / d.total) * 100) : 0;
      const missPercentage = 100 - accuracy;
      return { 
        domainName: d.name, 
        total: d.total, 
        correct: d.correct, 
        incorrect: d.total - d.correct, 
        accuracy, 
        missPercentage 
      };
    });

    scoredDomains.sort((a, b) => a.accuracy - b.accuracy);
    weakestDomain = scoredDomains[0];
  }

  const handlePracticeWeakestDomain = () => {
    if (!weakestDomain) {
      setActiveTab('review');
      return;
    }
    const domainName = weakestDomain.domainName;
    const domainQuestions = safeQuestions.filter(q => resolveDomainName(q?.domain, q?.id, recentPaperCode) === domainName);

    if (domainQuestions.length > 0 && onStartCustomPractice) {
      onStartCustomPractice(
        domainQuestions,
        `Smart Practice: ${domainName} Targeted Drill (${domainQuestions.length} Qs)`,
        'practice'
      );
    } else {
      setActiveTab('review');
    }
  };

  // -------------------------------------------------------------
  // INSIGHT WIDGET 2: Predicted Score Trajectory
  // -------------------------------------------------------------
  const scoredAttempts = [...safeAttempts].filter(a => a && a.completedAt).sort((a, b) => 
    new Date(a.completedAt).getTime() - new Date(b.completedAt).getTime()
  );

  let trajectoryChartData: any[] = [];
  let trajectorySummary: string = '';
  const hasEnoughAttemptsForTrajectory = scoredAttempts.length >= 3;

  if (hasEnoughAttemptsForTrajectory) {
    // Take up to last 10 attempts for regression
    const recentAttempts = scoredAttempts.slice(-10);
    const N = recentAttempts.length;

    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumXX = 0;

    for (let i = 0; i < N; i++) {
      const x = i + 1;
      const y = recentAttempts[i]?.score || (100 + Math.round((recentAttempts[i]?.percentage || 0) * 1.0));
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumXX += x * x;
    }

    const meanX = sumX / N;
    const meanY = sumY / N;
    const slope = (sumXX - N * meanX * meanX) !== 0 
      ? (sumXY - N * meanX * meanY) / (sumXX - N * meanX * meanX)
      : 0;
    const intercept = meanY - slope * meanX;

    // Build chart data
    for (let i = 0; i < N; i++) {
      const scoreVal = recentAttempts[i]?.score || (100 + Math.round((recentAttempts[i]?.percentage || 0) * 1.0));
      trajectoryChartData.push({
        name: `Test ${i + 1}`,
        actualScore: scoreVal,
        projectedScore: i === N - 1 ? scoreVal : null
      });
    }

    // Add 3 projected future sessions
    for (let p = 1; p <= 3; p++) {
      const xFut = N + p;
      const yProj = Math.min(200, Math.max(100, Math.round(slope * xFut + intercept)));
      trajectoryChartData.push({
        name: `Proj ${p}`,
        actualScore: null,
        projectedScore: yProj
      });
    }

    // Human-readable summary
    const latestScore = recentAttempts[N - 1]?.score || (100 + Math.round((recentAttempts[N - 1]?.percentage || 0) * 1.0));

    if (latestScore >= targetPassingScore && slope >= 0) {
      trajectorySummary = `At your current pace (${slope > 0 ? '+' + (Math.round(slope * 10) / 10) + ' pts/exam' : 'steady'}), you're consistently trending above the passing threshold of ${targetPassingScore}. Excellent momentum!`;
    } else if (slope > 0) {
      const passX = (targetPassingScore - intercept) / slope;
      const testsNeeded = Math.max(1, Math.ceil(passX - N));
      const weeksEst = Math.max(1, Math.ceil(testsNeeded * 0.75));
      trajectorySummary = `At your current pace (+${Math.round(slope * 10) / 10} pts/exam), you're projected to cross the passing threshold of ${targetPassingScore} in approximately ${weeksEst} week${weeksEst === 1 ? '' : 's'} (${testsNeeded} practice session${testsNeeded === 1 ? '' : 's'}).`;
    } else {
      trajectorySummary = `Your practice scores are currently averaging ${Math.round(meanY)} pts. Targeted drills in your weakest domain will help accelerate your score toward the ${targetPassingScore} passing target.`;
    }
  }

  const handleGenerateAiStudyPlan = async () => {
    setLoadingAiPlan(true);
    setAiPlan(null);
    try {
      const weakAreas = realDomainList.length > 0
        ? realDomainList.map(item => item.name)
        : [`${activeState.name} PRAXIS ${recentPaperCode} Core Benchmarks`];

      const res = await fetch('/api/ai/study-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weakAreas,
          targetState: activeState.name,
          targetExamDate: 'In 3 Weeks',
          paperName: `PRAXIS ${recentPaperCode}`
        })
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        console.error('[Dashboard AI Coach Error]:', res.status, data.error || data);
        setAiPlan(`⚠️ AI Study Planner Error: ${data.error || 'Failed to generate 7-day plan.'}`);
      } else {
        setAiPlan(data.plan || 'Plan generated successfully.');
      }
    } catch (err: any) {
      console.error('[Dashboard AI Coach Network Error]:', err);
      setAiPlan(`⚠️ Connection Error: Unable to contact AI study planner. (${err?.message || 'Network error'})`);
    } finally {
      setLoadingAiPlan(false);
    }
  };

  return (
    <div className="bg-pink-50 min-h-screen py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8">
      {/* Top Section: Welcome Banner + AI Study Plan / Daily Goal fitted alongside it */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Welcome Banner */}
        <div className="lg:col-span-2 relative overflow-hidden rounded-2xl bg-gradient-to-r from-pink-100 via-rose-100 to-pink-200 text-slate-900 p-6 sm:p-8 shadow-xl border border-pink-300/60">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-72 h-72 bg-rose-300/20 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col h-full justify-between gap-6">
            <div className="space-y-2 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-rose-700">
                <span className="px-2.5 py-1 rounded-full bg-white/70 border border-rose-300/60">
                  {activeState.name} ({activeState.code}) Standards Enabled
                </span>
                <span className="flex items-center gap-1 text-amber-600 font-bold">
                  <Flame className="w-3.5 h-3.5 fill-amber-500" /> {userProfile?.streakDays || 0}-Day Streak
                </span>
                {goalFamily && (
                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-700 font-bold">
                    <Star className="w-3 h-3 fill-emerald-500" /> Goal: {goalFamily.shortLabel}
                  </span>
                )}
                {daysToExam !== null && (
                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/80 border border-pink-300 text-pink-700 font-bold">
                    <Calendar className="w-3 h-3" />
                    {daysToExam > 1
                      ? `${daysToExam} days to your exam`
                      : daysToExam === 1
                      ? 'Your exam is tomorrow'
                      : daysToExam === 0
                      ? 'Exam day, good luck!'
                      : 'Your exam date has passed'}
                    {dailyGoalLabel && daysToExam >= 0 ? ` · ${dailyGoalLabel}` : ''}
                  </span>
                )}
              </div>

              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Welcome back, {userProfile?.displayName || 'Candidate'}!
              </h1>
              <p className="text-slate-600 text-sm leading-relaxed">
                Track your exam readiness, review spaced repetition intervals, and launch weakness-targeted practice drills aligned with {activeState.code} standards.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <button
                onClick={() => setActiveTab('mock-exams')}
                className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs sm:text-sm px-5 py-3 rounded-xl transition shadow-md flex items-center gap-2 border border-indigo-400/30 cursor-pointer"
              >
                <Play className="w-4 h-4 fill-white" />
                <span>Start Full Mock Exam</span>
              </button>
            </div>
          </div>
        </div>

        {/* AI Study Plan Box + Daily Goal Reminders, fitted next to the Welcome Banner */}
        <div className="lg:col-span-1 flex flex-col gap-4">
          {/* AI Study Plan Box */}
          <div className="flex-1 bg-gradient-to-br from-pink-100 via-rose-50 to-pink-100 text-slate-900 rounded-2xl p-5 shadow-lg border border-pink-300/60 space-y-3">
            <div className="flex items-center gap-2 text-rose-700 font-bold text-xs">
              <Sparkles className="w-4 h-4 text-amber-500" />
              AI Personal Exam Coach
            </div>

            <h3 className="text-base font-extrabold text-slate-900">
              Generate 7-Day Targeted Study Plan
            </h3>

            <p className="text-[11px] text-slate-600 leading-relaxed">
              Our Gemini AI analyzes your weak domains against {activeState.name} benchmarks to create a custom day-by-day study itinerary.
            </p>

            <button
              onClick={handleGenerateAiStudyPlan}
              disabled={loadingAiPlan}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-2 px-4 rounded-xl transition shadow-md flex items-center justify-center gap-2 text-xs"
            >
              {loadingAiPlan ? (
                <>Generating Plan...</>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" /> Generate Personalized Plan
                </>
              )}
            </button>

            {aiPlan && (
              <div className="mt-2 p-3 rounded-xl bg-white/70 border border-pink-200 text-[11px] text-slate-700 space-y-2 max-h-56 overflow-y-auto leading-relaxed">
                <div className="font-bold text-rose-700 border-b border-pink-200 pb-1 flex items-center justify-between">
                  <span>Custom Study Schedule</span>
                  <button onClick={() => setAiPlan(null)} className="text-[10px] text-slate-400 hover:underline">Close</button>
                </div>
                <div className="whitespace-pre-wrap">{aiPlan}</div>
              </div>
            )}
          </div>

          {/* Daily Goal & Push Notification Card */}
          <div className="bg-white rounded-2xl border border-pink-200 p-5 shadow-xs space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold shrink-0">
                <Calendar className="w-4.5 h-4.5" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900 text-xs">Daily Goal Reminders</h4>
                <p className="text-[11px] text-slate-500">Scheduled for 08:00 AM Daily</p>
              </div>
            </div>

            <p className="text-[11px] text-slate-600 leading-relaxed">
              Receive automated browser push notifications with daily high-yield questions to maintain your study streak.
            </p>

            <button
              onClick={() => setActiveTab('reminders')}
              className="w-full border border-pink-200 hover:bg-pink-50 text-slate-800 font-semibold py-2 rounded-xl text-xs transition"
            >
              Configure Notification Settings &rarr;
            </button>
          </div>
        </div>
      </div>

      {/* Spaced Repetition & Adaptive Practice Prompt Banner */}
      <div className="bg-white border border-pink-200 shadow-xs rounded-2xl p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center font-bold shrink-0 border border-amber-500/20">
            <RotateCcw className="w-6 h-6 text-amber-600" />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-slate-800">Spaced Repetition & Adaptive Practice</h3>
              {dueReviewCount > 0 ? (
                <span className="px-2.5 py-0.5 rounded-full bg-amber-500 text-slate-950 text-xs font-black animate-pulse">
                  {dueReviewCount} Questions Due
                </span>
              ) : (
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">
                  Queue Clear
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 leading-relaxed max-w-xl">
              SM-2 memory intervals optimize review timing (1d → 3d → 7d → 14d → 30d). Smart Practice targets your weakest domains using weighted adaptive question sampling.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <button
            onClick={() => setActiveTab('review')}
            className="flex-1 md:flex-none bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs px-4 py-2.5 rounded-xl transition shadow-xs flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Review Queue ({dueReviewCount})</span>
          </button>
          <button
            onClick={() => setActiveTab('review')}
            className="flex-1 md:flex-none bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition shadow-xs flex items-center justify-center gap-2"
          >
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span>Launch Smart Practice</span>
          </button>
        </div>
      </div>

      {/* Key Metric Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Card 1: Avg Practice Score */}
        <div className="bg-white p-5 rounded-xl border border-pink-200 shadow-xs hover:border-pink-300 transition">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Avg Practice Score
            </span>
            <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <Award className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-slate-900">
              {avgEtsScore !== null ? `${avgEtsScore} ETS` : '--'}
            </span>
            {avgEtsScore !== null ? (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                avgEtsScore >= targetPassingScore 
                  ? 'text-emerald-700 bg-emerald-50 border-emerald-200' 
                  : 'text-amber-700 bg-amber-50 border-amber-200'
              }`}>
                {avgEtsScore >= targetPassingScore ? 'Pass Ready' : 'Needs Practice'}
              </span>
            ) : (
              <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">
                No Data
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {totalAttempts > 0 
              ? `Based on ${totalAttempts} practice exam attempt${totalAttempts === 1 ? '' : 's'}`
              : 'Complete a practice session to see your average score'}
          </p>
        </div>

        {/* Card 2: Pass Predictor Index */}
        <div className="bg-white p-5 rounded-xl border border-pink-200 shadow-xs hover:border-pink-300 transition">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Pass Predictor Index
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className={`text-3xl font-extrabold ${
              passRate !== null 
                ? (passRate >= 75 ? 'text-emerald-600' : passRate >= 50 ? 'text-amber-600' : 'text-rose-600') 
                : 'text-slate-400'
            }`}>
              {passRate !== null ? `${passRate}%` : '--'}
            </span>
            {passRate !== null ? (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                passRate >= 75 
                  ? 'text-emerald-700 bg-emerald-100' 
                  : passRate >= 50 
                  ? 'text-amber-700 bg-amber-100' 
                  : 'text-rose-700 bg-rose-100'
              }`}>
                {passRate >= 75 ? 'High Confidence' : passRate >= 50 ? 'Moderate' : 'Low Confidence'}
              </span>
            ) : (
              <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                No Data
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {passRate !== null
              ? (passRate >= 50
                  ? `Exceeds ${activeState.code} minimum standard`
                  : `${passedAttemptsCount} of ${totalAttempts} attempt(s) met ${activeState.code} target (${targetPassingScore})`)
              : 'Take a practice exam to calculate prediction'}
          </p>
        </div>

        {/* Card 3: Study Time Logged */}
        <div className="bg-white p-5 rounded-xl border border-pink-200 shadow-xs hover:border-pink-300 transition">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Study Time Logged
            </span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-slate-900">
              {totalStudyMinutes > 0
                ? `${hoursLogged > 0 ? `${hoursLogged}h ` : ''}${minsLogged}m`
                : '0h 0m'}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {totalStudyMinutes > 0
              ? 'Tracked from exam simulations & practice sessions'
              : 'No study activity recorded yet'}
          </p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-pink-200 shadow-xs hover:border-pink-300 transition">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Membership Access
            </span>
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <BookOpen className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-extrabold text-slate-900">
              {userProfile?.tier && userProfile.tier !== 'free' ? 'Full Access' : 'Free Preview'}
            </span>
            {(!userProfile?.tier || userProfile.tier === 'free') && (
              <button 
                onClick={openPricingModal} 
                className="text-xs font-semibold text-amber-600 underline hover:text-amber-700 cursor-pointer"
              >
                Subscribe
              </button>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {userProfile?.tier && userProfile.tier !== 'free' ? 'All Subjects & Mock Exams Unlocked' : 'Upgrade to Monthly All-Access'}
          </p>
        </div>
      </div>

      {/* ========================================================= */}
      {/* STATE CERTIFICATION EXAM SYSTEMS -- prioritized directly    */}
      {/* after the key metric cards, ahead of the insight widgets   */}
      {/* ========================================================= */}
      <div className="bg-white rounded-2xl border border-pink-200 shadow-xs p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            State Certification Exam Systems
          </h3>
          <button
            onClick={() => setActiveTab('papers')}
            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
          >
            View Full Directory &rarr;
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...examCatalog]
            .sort((a, b) => {
              if (!goalFamily) return 0;
              if (a.id === goalFamily.id) return -1;
              if (b.id === goalFamily.id) return 1;
              return 0;
            })
            .map(entry => (
            <div
              key={entry.id}
              onClick={() => {
                if (onSelectExam) {
                  onSelectExam(entry);
                } else {
                  setActiveTab('papers');
                }
              }}
              className={`p-5 rounded-xl border-2 bg-emerald-600 hover:bg-emerald-500 hover:shadow-lg transition cursor-pointer flex flex-col justify-between space-y-3 group min-h-[180px] ${
                goalFamily?.id === entry.id ? 'border-blue-300 ring-2 ring-blue-200' : 'border-emerald-700'
              }`}
            >
              <div>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-extrabold text-sm text-blue-50 group-hover:text-white transition-colors flex items-center gap-1.5">
                    {entry.state}
                    {goalFamily?.id === entry.id && (
                      <span className="flex items-center gap-0.5 text-[9px] font-black text-blue-900 bg-blue-200 px-1.5 py-0.5 rounded border border-blue-300 uppercase tracking-wider">
                        <Star className="w-2.5 h-2.5 fill-blue-900" /> Your Goal
                      </span>
                    )}
                  </span>
                  <span className="text-[10px] font-bold text-blue-50 bg-emerald-700 px-2 py-0.5 rounded border border-emerald-400 shrink-0">
                    {(entry.examCodes || []).length} Sub-Exams
                  </span>
                </div>
                <p className="text-xs text-blue-50/90 font-medium line-clamp-1 mt-1">
                  {entry.examLabel}
                </p>
              </div>

              <div className="flex flex-wrap gap-1 pt-1">
                {(entry.examCodes || []).map(code => (
                  <span key={code} className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-700 text-blue-100 border border-emerald-400">
                    {code}
                  </span>
                ))}
              </div>

              <div className="pt-2 border-t border-emerald-400/60 flex items-center justify-between text-xs font-bold text-blue-100">
                <span>Explore {entry.state} Exams</span>
                <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1 text-blue-100" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ========================================================= */}
      {/* INSIGHT WIDGETS SECTION: ERROR PATTERN & SCORE TRAJECTORY */}
      {/* ========================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* WIDGET 1: ERROR PATTERN INSIGHT */}
        <div className="bg-white rounded-2xl border border-pink-200 shadow-xs p-6 flex flex-col justify-between space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-pink-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
                <Target className="w-4 h-4 text-rose-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Error Pattern Analysis</h3>
                <p className="text-[11px] text-slate-500">Domain-level accuracy diagnostic</p>
              </div>
            </div>

            <span className="px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-[11px] font-bold">
              AI Diagnostic
            </span>
          </div>

          {weakestDomain ? (
            <div className="bg-gradient-to-r from-rose-50/80 via-amber-50/40 to-rose-50/80 border border-rose-200/80 rounded-xl p-5 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="text-xs font-bold uppercase tracking-wider text-rose-800">
                    Highest Opportunity Focus
                  </div>
                  <h4 className="text-sm font-black text-slate-900">
                    {weakestDomain.domainName}
                  </h4>
                </div>
                <span className="px-2.5 py-1 bg-rose-600 text-white rounded-full text-xs font-extrabold shrink-0">
                  {weakestDomain.missPercentage}% Missed
                </span>
              </div>

              <p className="text-xs text-slate-700 leading-relaxed font-medium">
                You're missing <span className="font-extrabold text-rose-700">{weakestDomain.missPercentage}%</span> of <span className="font-bold text-slate-900">{weakestDomain.domainName}</span> questions — this is your biggest opportunity to boost your overall score.
              </p>

              <div className="flex items-center justify-between text-[11px] text-slate-500 font-semibold pt-1">
                <span>
                  {weakestDomain.incorrect} missed out of {weakestDomain.total} attempts ({weakestDomain.accuracy}% accuracy)
                </span>
              </div>

              <button
                onClick={handlePracticeWeakestDomain}
                className="w-full mt-2 bg-rose-600 hover:bg-rose-700 text-white font-extrabold text-xs py-2.5 px-4 rounded-xl transition shadow-xs flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span>Practice {weakestDomain.domainName}</span>
              </button>
            </div>
          ) : (
            <div className="bg-pink-50/60 border border-pink-200/80 rounded-xl p-6 text-center space-y-3 my-auto">
              <HelpCircle className="w-8 h-8 text-slate-400 mx-auto" />
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-slate-800">
                  Error Pattern Analysis
                </h4>
                <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
                  Complete at least 5 practice questions in a domain to unlock personalized error pattern diagnostics and targeted weakness drills.
                </p>
              </div>
              <div className="pt-1">
                <button
                  onClick={() => setActiveTab('review')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-4 py-2 rounded-xl transition shadow-xs"
                >
                  Start Practice Session
                </button>
              </div>
            </div>
          )}
        </div>

        {/* WIDGET 2: PREDICTED SCORE TRAJECTORY */}
        <div className="bg-white rounded-2xl border border-pink-200 shadow-xs p-6 flex flex-col justify-between space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-pink-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Predicted Score Trajectory</h3>
                <p className="text-[11px] text-slate-500">Linear regression score model</p>
              </div>
            </div>

            <span className="px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[11px] font-bold">
              Pass Target: {targetPassingScore}
            </span>
          </div>

          {hasEnoughAttemptsForTrajectory ? (
            <div className="space-y-3">
              <div className="h-52 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trajectoryChartData} margin={{ top: 15, right: 15, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis domain={[100, 200]} tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip 
                      formatter={(val: any, name: any) => [`${val} pts`, name === 'actualScore' ? 'Actual Score' : 'Projected Score']}
                      contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', fontSize: '12px' }}
                    />
                    <ReferenceLine 
                      y={targetPassingScore} 
                      stroke="#ef4444" 
                      strokeDasharray="4 4" 
                      label={{ value: `Pass Threshold (${targetPassingScore})`, fill: '#ef4444', fontSize: 11, position: 'top' }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="actualScore" 
                      stroke="#4f46e5" 
                      strokeWidth={3} 
                      dot={{ r: 4, fill: '#4f46e5' }} 
                      name="actualScore" 
                      connectNulls 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="projectedScore" 
                      stroke="#f59e0b" 
                      strokeWidth={2} 
                      strokeDasharray="5 5" 
                      dot={{ r: 4, fill: '#f59e0b' }} 
                      name="projectedScore" 
                      connectNulls 
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="p-3 bg-pink-50/60 border border-pink-200/80 rounded-xl text-xs text-slate-700 font-medium flex items-center gap-2.5">
                <TrendingUp className="w-4 h-4 text-indigo-600 shrink-0" />
                <span>{trajectorySummary}</span>
              </div>
            </div>
          ) : (
            <div className="bg-pink-50/60 border border-pink-200/80 rounded-xl p-6 text-center space-y-3 my-auto">
              <TrendingUp className="w-8 h-8 text-slate-400 mx-auto" />
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-slate-800">
                  Predicted Score Trajectory
                </h4>
                <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
                  Complete a few more practice exams to see your projected trajectory and estimated time to cross the passing score threshold.
                </p>
              </div>
              <div className="pt-1">
                <button
                  onClick={() => setActiveTab('tests')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-4 py-2 rounded-xl transition shadow-xs"
                >
                  Take Practice Exam
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Grid: Weak Area Analysis & AI Coach */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column (2/3): Domain Mastery & Featured Papers */}
        <div className="lg:col-span-2 space-y-6">
          {/* Domain Mastery Breakdown */}
          <div className="bg-white rounded-2xl border border-pink-200 shadow-xs p-6 space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-pink-100">
              <div>
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <BrainCircuit className="w-5 h-5 text-indigo-600" />
                  Domain Mastery & Benchmark Breakdown
                </h2>
                <p className="text-xs text-slate-500">
                  Identified from your recent practice drills against state benchmarks
                </p>
              </div>

              <button 
                onClick={() => setActiveTab('tests')}
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 self-start sm:self-auto"
              >
                Take Targeted Drill <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              {realDomainList.length > 0 ? (
                realDomainList.map((item, idx) => (
                  <div key={idx} className="space-y-1.5 p-3 rounded-lg bg-pink-50/50 border border-pink-100">
                    <div className="flex items-center justify-between text-xs font-medium">
                      <span className="text-slate-800 font-semibold flex items-center gap-1.5">
                        {item.mastery < 70 && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                        {item.name}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-slate-500">{item.count} Questions Drilled</span>
                        <span className={`font-bold ${item.mastery < 70 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {item.mastery}%
                        </span>
                      </div>
                    </div>

                    <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                      <div 
                        className={`h-2.5 rounded-full transition-all duration-500 ${item.color}`}
                        style={{ width: `${item.mastery}%` }}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center bg-pink-50/60 border border-pink-200/80 rounded-xl space-y-3">
                  <BrainCircuit className="w-8 h-8 text-slate-400 mx-auto" />
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-slate-800">No Domain Performance Data Yet</h4>
                    <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
                      Complete practice questions or exams to see your real domain breakdown by topic domain against state standards.
                    </p>
                  </div>
                  <button
                    onClick={() => setActiveTab('tests')}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-4 py-2 rounded-xl transition shadow-xs"
                  >
                    Start Practice Exam
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column (1/3): Tier Access Reminder */}
        <div className="space-y-6">
          {/* Tier Access Banner */}
          {(!userProfile?.tier || userProfile.tier === 'free') && (
            <div className="bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-amber-500/10 border border-amber-500/30 rounded-2xl p-6 text-slate-900 space-y-3">
              <div className="flex items-center gap-2 text-amber-800 font-bold text-sm">
                <Sparkles className="w-4 h-4 text-amber-600" />
                Unlock All-Access Subscription
              </div>
              <p className="text-xs text-slate-700 leading-relaxed">
                Choose an All-Access Plan for full Praxis & State exam preparation, constructed response grading, and unlimited AI tutoring.
              </p>
              <button
                onClick={openPricingModal}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-2 rounded-xl text-xs transition shadow-xs"
              >
                View Pricing & Subscription Options
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};